import { useEffect, useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { useGlobalStoreShallow } from 'src/store/globalStore';

import { ModalsProvider, notify } from '@speeki/global-ui-components';
import { SessionStorageManager } from '@speeki/storage-manager';

import { GlobalTenantDataByReportingSlugDocument } from '@graphql/generated/graphql';

import { checkIfTeams } from '@utils/helpers';
import { useQueryParam } from '@utils/hooks/useQueryParam';

import { APIService } from '@services/ApiService';
import { AuthService } from '@services/AuthService/AuthService';
import { getPath } from '@services/RouteService/paths';

import { useLazyQuery, useReactiveVar } from '@apollo/client';
import {
    authVar,
    subsidiarySiteUuidFromUrlVar,
    switchCompanyMetadataVar,
} from '@boot/reactiveVariables';
import { ERROR_MESSAGES } from '@shared/notifyMessages';

export const useRedirectByCompanySlug = () => {
    const { setCompanyInfo } = useGlobalStoreShallow(['setCompanyInfo']);

    const navigate = useNavigate();
    const { isLoggedIn } = useReactiveVar(authVar);
    const [redirectLoading, setRedirectLoading] = useState(true);
    const currentTenantUuid = AuthService.getTenantUuid();

    const [getTenantDataByReportingSlug] = useLazyQuery(
        GlobalTenantDataByReportingSlugDocument,
        {
            context: {
                globalUrl: true,
            },
            fetchPolicy: 'cache-and-network',
            onCompleted: (data) => {
                const {
                    tenant: { apiUrl, code, name, subsidiarySiteUuid, uuid },
                } = data;

                if (isLoggedIn && currentTenantUuid !== uuid) {
                    setRedirectLoading(false);
                    switchCompanyMetadataVar({
                        uuid,
                        apiUrl,
                        code,
                        currentTenantUuid,
                        name,
                        subsidiarySiteUuid,
                    });
                    ModalsProvider.open('SwitchCompanyModal');
                    return;
                }

                setCompanyInfo({
                    uuid,
                    apiUrl,
                    code,
                    name,
                });

                if (subsidiarySiteUuid) {
                    subsidiarySiteUuidFromUrlVar(subsidiarySiteUuid);
                    SessionStorageManager.setValue(
                        'subsidiarySite',
                        subsidiarySiteUuid,
                    );
                }

                new APIService().setGraphAPI = apiUrl;
                AuthService.setTenantUuid(uuid);

                setRedirectLoading(false);
            },
            onError: () => {
                notify(ERROR_MESSAGES.auth.tenantNotExist).ERROR();
                void AuthService.logout(false);
                navigate(getPath({ path: '/auth/welcome' }), { replace: true });
                setRedirectLoading(false);
            },
        },
    );

    const hideDownloadAppModal = SessionStorageManager.getValue(
        'hideDownloadAppModal',
    );

    const companySlug = useQueryParam('companySlug');

    const showDownloadAppModal =
        (isAndroid || isIOS) &&
        !checkIfTeams() &&
        !isLoggedIn &&
        !hideDownloadAppModal;

    useEffect(() => {
        if (!companySlug) {
            setRedirectLoading(false);
            return;
        }

        showDownloadAppModal && ModalsProvider.open('DownloadSpeekiAppModal');

        void getTenantDataByReportingSlug({
            variables: { slug: companySlug },
        });
    }, []);

    return {
        redirectLoading,
    };
};
